import { api } from '../axios';
import {
  DebtAdjustmentVoucherResponse,
  CreateDebtAdjustmentVoucherRequest,
  UpdateDebtAdjustmentVoucherRequest,
  DebtAdjustmentVoucherListQuery,
  PaginatedDebtAdjustmentVoucherResponse,
  DebtAdjustmentVoucherStatusCounts
} from '@/types/debtAdjustmentVoucher';

export interface ApiResponse<T> {
  data: T;
  message: string;
  timestamp: string;
}

export const debtAdjustmentVoucherApi = {
  // Create a new debt adjustment voucher
  createDebtAdjustmentVoucher: async (request: CreateDebtAdjustmentVoucherRequest): Promise<DebtAdjustmentVoucherResponse> => {
    const response = await api.post<ApiResponse<DebtAdjustmentVoucherResponse>>('/debt-adjustment-vouchers', request);
    return response.data.data;
  },

  // Get all debt adjustment vouchers with pagination and filtering
  getAllDebtAdjustmentVouchers: async (query: DebtAdjustmentVoucherListQuery = {}): Promise<PaginatedDebtAdjustmentVoucherResponse> => {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (query.page) params.append('page', query.page.toString());
    if (query.page_size) params.append('page_size', query.page_size.toString());
    
    // Add search parameter
    if (query.search) params.append('search', query.search);
    
    // Add sorting parameters
    if (query.sort_by) params.append('sort_by', query.sort_by);
    if (query.sort_order) params.append('sort_order', query.sort_order);
    
    // Add filter parameters
    if (query.entity_type) params.append('entity_type', query.entity_type);
    if (query.entity_id) params.append('entity_id', query.entity_id);
    if (query.status && query.status !== 'all') params.append('status', query.status);
    if (query.date_from) params.append('date_from', query.date_from);
    if (query.date_to) params.append('date_to', query.date_to);

    const response = await api.get<PaginatedDebtAdjustmentVoucherResponse>(`/debt-adjustment-vouchers?${params.toString()}`);
    return response.data;
  },

  // Get a specific debt adjustment voucher by code
  getDebtAdjustmentVoucher: async (code: string): Promise<DebtAdjustmentVoucherResponse> => {
    const response = await api.get<ApiResponse<DebtAdjustmentVoucherResponse>>(`/debt-adjustment-vouchers/${code}`);
    return response.data.data;
  },

  // Update an existing debt adjustment voucher
  updateDebtAdjustmentVoucher: async (code: string, request: UpdateDebtAdjustmentVoucherRequest): Promise<DebtAdjustmentVoucherResponse> => {
    const response = await api.put<ApiResponse<DebtAdjustmentVoucherResponse>>(`/debt-adjustment-vouchers/${code}`, request);
    return response.data.data;
  },

  // Delete a debt adjustment voucher
  deleteDebtAdjustmentVoucher: async (code: string): Promise<void> => {
    await api.delete(`/debt-adjustment-vouchers/${code}`);
  },

  // Confirm a debt adjustment voucher (change status to confirmed)
  confirmDebtAdjustmentVoucher: async (code: string): Promise<DebtAdjustmentVoucherResponse> => {
    const response = await api.post<ApiResponse<DebtAdjustmentVoucherResponse>>(`/debt-adjustment-vouchers/${code}/confirm`);
    return response.data.data;
  },

  // Cancel a debt adjustment voucher (change status to cancelled)
  cancelDebtAdjustmentVoucher: async (code: string): Promise<DebtAdjustmentVoucherResponse> => {
    const response = await api.post<ApiResponse<DebtAdjustmentVoucherResponse>>(`/debt-adjustment-vouchers/${code}/cancel`);
    return response.data.data;
  },

  // Get status counts for filter tabs using optimized endpoint
  getStatusCounts: async (filters: Partial<DebtAdjustmentVoucherListQuery> = {}): Promise<DebtAdjustmentVoucherStatusCounts> => {
    try {
      const params = new URLSearchParams();

      // Add filter parameters (excluding status, page, page_size, sort_by, sort_order)
      if (filters.search) params.append('search', filters.search);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.entity_id) params.append('entity_id', filters.entity_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await api.get<ApiResponse<DebtAdjustmentVoucherStatusCounts>>(`/debt-adjustment-vouchers/status-counts?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching status counts:', error);
      return {
        all: 0,
        created: 0,
        confirmed: 0,
        cancelled: 0
      };
    }
  },
};

